import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type {
  TypeMouvement,
  EtatMouvement,
  StatutOeuvre,
  MomentConstat,
  EtatConstat,
  Assurance,
  SuiviTransport,
  SourceValeurProposee,
  PoliceProposee,
} from '@artosera/shared';
import { api, requeteMultipart, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';
import type { MediaPresente } from './medias.js';
export interface LigneMouvementPresentee {
  oeuvreId: string;
  titre: string;
  artisteNom: string;
  numeroInventaire: string;
  mediaPrincipalId: string | null;
  apercuUrl?: string;
  prixPublicCentimes: number | null;
  statut: StatutOeuvre | null;
  localisationAvantId: string | null;
  statutAvant: StatutOeuvre | null;
  statutPose: StatutOeuvre | null;
  retourneeLe: string | null;
  ignoreeAuRetour: StatutOeuvre | null;
}

export interface ConstatPresente {
  id: string;
  oeuvreId: string;
  moment: MomentConstat;
  etatGeneral: EtatConstat;
  observations: string;
  etabliLe: string;
  etabliPar: string;
  photos: string[];
  documentId: string | null;
}

export interface MouvementPresente {
  id: string;
  caisseIds?: string[];
  type: TypeMouvement;
  etat: EtatMouvement;
  enRetard: boolean;
  joursDeRetard: number;
  lignes: LigneMouvementPresentee[];
  deId: string | null;
  versId: string | null;
  contactId: string | null;
  contactNom?: string;
  transporteurId: string | null;
  departLe: string;
  retourPrevuLe: string | null;
  retourReelLe: string | null;
  assurance: Assurance | null;
  valeurProposeeCentimes: number;
  valeurProposeeSource?: SourceValeurProposee;
  policeProposee?: PoliceProposee;
  suivi: SuiviTransport | null;
  bon: { numero: string; emisLe: string; documentId: string | null } | null;
  constats: ConstatPresente[];
  note: string;
  annuleLe: string | null;
  creeLe: string;
  modifieLe: string;
}

export interface CorpsMouvement {
  depotRecu?: boolean;
  type: TypeMouvement;
  oeuvres: string[];
  departLe: string;
  deId?: string | null;
  versId?: string | null;
  contactId?: string | null;
  transporteurId?: string | null;
  retourPrevuLe?: string | null;
  assurance?: Assurance;
  suivi?: SuiviTransport;
  note?: string;
}
export interface CorpsConstat {
  oeuvreId: string;
  moment: MomentConstat;
  etatGeneral: EtatConstat;
  observations?: string;
  etabliLe?: string;
  etabliPar: string;
}
export interface FiltresMouvements {
  type?: TypeMouvement;
  etat?: readonly EtatMouvement[];
  enRetard?: boolean;
  oeuvreId?: string;
  contactId?: string;
  page?: number;
  parPage?: number;
}
export interface CorpsGeste {
  oeuvres?: string[];
  departLe?: string;
  retourLe?: string;
  arriveLe?: string;
  versId?: string | null;
  raison?: string;
}
type ReponseMouvement = {
  mouvement: MouvementPresente;
  ignorees?: { oeuvreId: string; statut: StatutOeuvre }[];
};
const racine = (g: string) => `/g/${encodeURIComponent(g)}/mouvements`;
const fiche = (g: string, id: string) => `${racine(g)}/${encodeURIComponent(id)}`;
function filtresQuery(f: FiltresMouvements): string {
  const p = new URLSearchParams({ page: String(f.page ?? 1), parPage: String(f.parPage ?? 50) });
  if (f.type) p.set('type', f.type);
  if (f.etat?.length) p.set('etat', f.etat.join(','));
  if (f.enRetard) p.set('enRetard', 'true');
  if (f.oeuvreId) p.set('oeuvreId', f.oeuvreId);
  if (f.contactId) p.set('contactId', f.contactId);
  return p.toString();
}
const geste = (g: string, id: string, action: string, corps: CorpsGeste, appel?: AppelApi) =>
  api.post<ReponseMouvement>(`${fiche(g, id)}/${action}`, corps, { galerie: g, ...appel });
export const mouvementsApi = {
  lister: (g: string, f: FiltresMouvements = {}, appel?: AppelApi) =>
    api.get<{ mouvements: MouvementPresente[]; pagination: Pagination }>(
      `${racine(g)}?${filtresQuery(f)}`,
      { galerie: g, ...appel },
    ),
  obtenir: (g: string, id: string, appel?: AppelApi) =>
    api.get<ReponseMouvement>(fiche(g, id), { galerie: g, ...appel }),
  creer: (g: string, corps: CorpsMouvement, appel?: AppelApi) =>
    api.post<ReponseMouvement>(racine(g), corps, { galerie: g, ...appel }),
  modifier: (
    g: string,
    id: string,
    corps: Partial<Omit<CorpsMouvement, 'type'>>,
    appel?: AppelApi,
  ) => api.patch<ReponseMouvement>(fiche(g, id), corps, { galerie: g, ...appel }),
  depart: (g: string, id: string, corps: CorpsGeste = {}, appel?: AppelApi) =>
    geste(g, id, 'depart', corps, appel),
  retour: (g: string, id: string, corps: CorpsGeste = {}, appel?: AppelApi) =>
    geste(g, id, 'retour', corps, appel),
  arrivee: (g: string, id: string, corps: CorpsGeste = {}, appel?: AppelApi) =>
    geste(g, id, 'arrivee', corps, appel),
  annuler: (g: string, id: string, corps: CorpsGeste, appel?: AppelApi) =>
    geste(g, id, 'annuler', corps, appel),
  emettreBon: (g: string, id: string, appel?: AppelApi) =>
    api.post<ReponseMouvement & { document: ReponseDocumentSigne }>(
      `${fiche(g, id)}/bon`,
      {},
      { galerie: g, ...appel },
    ),
  bonPdf: (g: string, id: string, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(`${fiche(g, id)}/bon.pdf`, { galerie: g, ...appel }),
  ajouterConstat: (g: string, id: string, corps: CorpsConstat, appel?: AppelApi) =>
    api.post<{ constat: ConstatPresente }>(`${fiche(g, id)}/constats`, corps, {
      galerie: g,
      ...appel,
    }),
  ajouterPhoto: (g: string, id: string, constatId: string, fichier: File, appel?: AppelApi) => {
    const corps = new FormData();
    corps.append('fichier', fichier);
    return requeteMultipart<{ constat: ConstatPresente; media: MediaPresente }>(
      `${fiche(g, id)}/constats/${encodeURIComponent(constatId)}/photos`,
      corps,
      { galerie: g, ...appel },
    );
  },
  constatPdf: (g: string, id: string, constatId: string, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(`${fiche(g, id)}/constats/${encodeURIComponent(constatId)}/pdf`, {
      galerie: g,
      ...appel,
    }),
  versCoffre: (
    g: string,
    id: string,
    corps: { piece: 'bon' | 'constat'; constatId?: string },
    appel?: AppelApi,
  ) => api.post<{ documentId: string }>(`${fiche(g, id)}/coffre`, corps, { galerie: g, ...appel }),
};
export function useMouvements(g: string, f: FiltresMouvements = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'mouvements', 'liste', f),
    queryFn: (c) => mouvementsApi.lister(g, f, appelDeRequete(c)),
  });
}
export function useMouvement(g: string, id: string | null) {
  return useQuery({
    queryKey: cleMetier(g, 'mouvements', 'fiche', id),
    queryFn: (c) => mouvementsApi.obtenir(g, id ?? '', appelDeRequete(c)),
    select: (r) => r.mouvement,
    enabled: id !== null,
  });
}
function useMutationMouvement<T, R>(g: string, fn: (v: T, a: AppelApi) => Promise<R>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: fn,
      onSuccess: () => {
        for (const collection of ['mouvements', 'oeuvres', 'documents'])
          void client.invalidateQueries({ queryKey: cleMetier(g, collection) });
      },
    }),
  );
}
export function useCreerMouvement(g: string) {
  return useMutationMouvement(g, (c: CorpsMouvement, a) => mouvementsApi.creer(g, c, a));
}
export function useModifierMouvement(g: string) {
  return useMutationMouvement(
    g,
    (v: { id: string; corps: Partial<Omit<CorpsMouvement, 'type'>> }, a) =>
      mouvementsApi.modifier(g, v.id, v.corps, a),
  );
}
export function useGesteMouvement(g: string) {
  return useMutationMouvement(
    g,
    (
      v: { id: string; geste: 'depart' | 'retour' | 'arrivee' | 'annuler'; corps?: CorpsGeste },
      a,
    ) => mouvementsApi[v.geste](g, v.id, v.corps ?? {}, a),
  );
}
export function useEmettreBon(g: string) {
  return useMutationMouvement(g, (id: string, a) => mouvementsApi.emettreBon(g, id, a));
}
export function useAjouterConstat(g: string) {
  return useMutationMouvement(g, (v: { id: string; corps: CorpsConstat }, a) =>
    mouvementsApi.ajouterConstat(g, v.id, v.corps, a),
  );
}
export function useAjouterPhoto(g: string) {
  return useMutationMouvement(g, (v: { id: string; constatId: string; fichier: File }, a) =>
    mouvementsApi.ajouterPhoto(g, v.id, v.constatId, v.fichier, a),
  );
}
export function useVersCoffre(g: string) {
  return useMutationMouvement(
    g,
    (v: { id: string; piece: 'bon' | 'constat'; constatId?: string }, a) =>
      mouvementsApi.versCoffre(
        g,
        v.id,
        { piece: v.piece, ...(v.constatId ? { constatId: v.constatId } : {}) },
        a,
      ),
  );
}
